import { IUserUpdateInputModel, NotificationPreferencesInputModel } from 'inputModels';
import { useNavigate } from 'react-router-dom';
import { serialize } from 'object-to-formdata';
import { useEffect, useRef, useState } from 'react';
import {
  IRefRegisterPersonalData,
  IRefRegisterProfileData,
  IRefRegisterServiceData,
  IRefRegisterTypeProfile,
  IRefRegisterUrlData,
  IRefRegisterAddressData,
  IRefRegisterTypeServiceData,
} from 'entities';
import { IAddressViewModel } from 'viewModels';
import { BasicButton, NavHeader } from '../../components';
import { RegisterProfile } from '../../components/RegisterProfile';
import { RegisterService } from '../../components/RegisterService';
import { RegisterTypeProfile } from '../../components/RegisterTypeProfile';
import { RegisterUrl } from '../../components/RegisterUrl';
import { useApp, useAuth, useRegisterForm } from '../../context';
import { UserController } from '../../controller/UserController';
import { TutorialType, TypeContact, TypeUser, UserProfileType } from '../../types/enums';
import { unMask } from '../../utils/removeMask';
import { Content } from './styles';
import { useAlert } from '../../hooks/useAlert';
import { useSpecialties } from '../../context/specialties';
import { useCorrespondences } from '../../context/correspondences';
import { useDistricts } from '../../context/districts';
import { RegisterPersonalData } from '../../components/RegisterPersonalData';
import { RegisterAddress } from '../../components/RegisterAddress';
import { RegisterTypeServices } from '../../components/RegisterTypeServices';
import { InviteFriend } from '../../components/InviteFriend';
import JoyrideTour from '../../components/JoyrideTour';
import TitlePage from '../../components/TitlePage';
import { handleGetAddressError } from '../../utils/handleGetAddressError';
import {
  isValidExternalContact,
  prepareExternalContactsForSubmit,
} from '../../utils/validateExternalContact';

export function BaseUserComplementRegister() {
  const { setUser, user } = useAuth();
  const { districtsSelectedsByUser } = useDistricts();
  const [runningTutorial, setRunningTutorial] = useState(false);
  const navigate = useNavigate();
  const alert = useAlert();
  const app = useApp();
  const personalDataRef = useRef<IRefRegisterPersonalData>(null);
  const profileTypeDataRef = useRef<IRefRegisterTypeProfile>(null);
  const addressDataRef = useRef<IRefRegisterAddressData>(null);
  const serviceDataRef = useRef<IRefRegisterServiceData>(null);
  const serviceTypeDataRef = useRef<IRefRegisterTypeServiceData>(null);
  const profileDataRef = useRef<IRefRegisterProfileData>(null);
  const urlDataRef = useRef<IRefRegisterUrlData>(null);
  const {
    setFormSubmited,
    setActiveForm,
    setDocument,
    setFirstName,
    setLastName,
    setIsVerified,
    setInscriptionNumber,
    setTypeProfile,
    setTypeValidation,
    setVerificationPictures,
    setTradingName,
    setAddress,
    setIsServiceProvider,
    setPresentationLetter,
    setPictureProfile,
    setExternalContacts,
    setLinkFacebook,
    setLinkInstagram,
    setLinkLinkedIn,
    setLinkSite,
    setLinkTwitter,
    setUrl,
    userType,
    typeProfile,
    setRegistrationOrigin,
    setUserType,
  } = useRegisterForm();
  const { setSpecialtiesSelectedByUser } = useSpecialties();
  const { setCorrespondencesSelectedByUser } = useCorrespondences();

  const steps = [
    {
      target: '#start-item',
      content:
        'Esta página é destinada a finalização do seu cadastro, preencha todos os campos obrigatórios para finalizar o cadastro.',
      disableBeacon: true,
    },
    {
      target: '#document-input',
      content:
        'Digite seu documento de identificação, caso seja pessoa física, digite seu CPF, caso seja pessoa jurídica, digite seu CNPJ.',
    },
    {
      target: '#name-input',
      content:
        'Após inserir seu documento, seu nome deve aparecer automaticamente, caso não apareça, digite seu nome completo.',
    },

    {
      target: '#expand-register-service',
      content:
        'Você pode clicar para expandir esta aba e selecionar se deseja prestar ou não serviços, se for prestar serviços, selecione suas comarcas de atuação.',
    },
    {
      target: '#select-specialties',
      content: 'Selecione as suas especialidades, você pode selecionar mais de uma especialidade.',
    },
    {
      target: '#select-correspondences',
      content:
        'Selecione as correspondências que você deseja prestar serviços, você pode selecionar mais de uma correspondência.',
    },
    {
      target: '#contacts',
      content:
        'Aqui você pode adicionar contatos externos, como telefone, email, whatsapp, entre outros. e configurar suas redes sociais',
    },
    {
      target: '#profile-url',
      content: 'Aqui você pode adicionar mudar a URL de referencia para o seu perfil.',
    },
    {
      target: '#invite-friends',
      content: 'Aqui você pode convidar outras pessoas para a plataforma.',
    },
    {
      target: '#save-button',
      content: 'Clique aqui para salvar as alterações.',
    },
  ];

  const handleUpdateProfile = async () => {
    setFormSubmited(true);
    const personalData = await personalDataRef.current?.handleUpdatePersonalData();
    const profileTypeData = profileTypeDataRef.current?.handleUpdateTypeProfile();
    const addressData = addressDataRef.current?.handleUpdateAddressData();
    const serviceData = serviceDataRef.current?.handleUpdateServiceData();
    const serviceTypeData = serviceTypeDataRef.current?.handleUpdateTypeServiceData();
    const profileData = await profileDataRef.current?.handleUpdateProfileData();
    const urlData = await urlDataRef.current?.handleUpdateUrlData();
    if (profileTypeData?.hasError) {
      profileTypeDataRef.current?.handleFocusOnError();
      setActiveForm('registerTypeProfile');
      return;
    }
    // if (profileTypeData?.typeProfile === UserProfileType.LAWYER && !profileTypeData?.isVerified) {
    //   setActiveForm('registerTypeProfile');
    //   alert.show({
    //     message: 'Não é possível finalizar o cadastro sem a verificação do perfil!',
    //     type: 'error',
    //   });
    //   return;
    // }
    if (!personalData || !serviceData || !serviceTypeData || !profileData || !urlData) {
      alert.show({
        message: 'Falha ao tentar buscar os dados do formulário, tente novamente!',
        type: 'error',
      });
      return;
    }

    const hasError =
      serviceData.hasError ||
      urlData.hasError ||
      personalData.hasError ||
      addressData?.hasError ||
      serviceTypeData.hasError ||
      profileData.hasError ||
      profileTypeData?.hasError;

    if (hasError) {
      if (personalData.hasError) {
        personalDataRef.current?.handleFocusOnError();
        return;
      }
      if (profileTypeData?.hasError) {
        setActiveForm('registerTypeProfile');
        profileTypeDataRef.current?.handleFocusOnError();
        return;
      }
      if (addressData?.hasError) {
        setActiveForm('registerAddress');
        addressDataRef.current?.handleFocusOnError();
        return;
      }
      if (serviceData.hasError) {
        setActiveForm('registerService');
        serviceDataRef.current?.handleFocusOnError();
        return;
      }
      if (serviceTypeData.hasError) {
        serviceTypeDataRef.current?.handleFocusOnError();
        setActiveForm('registerTypeService');
        return;
      }
      if (profileData.hasError) {
        setActiveForm('registerProfile');
        profileDataRef.current?.handleFocusOnError();
        return;
      }
      return;
    }
    const update: IUserUpdateInputModel = {
      firstName: personalData.firstName,
      lastName: personalData.lastName,
      email: personalData.email,
      document: unMask(personalData.document),
      birthDate: personalData.birthDate,
      isVerified: profileTypeData?.isVerified,
      inscriptionNumber: profileTypeData?.inscriptionNumber,
      profileType: profileTypeData?.typeProfile,
      ufInscriptionNumber: profileTypeData?.ufInscriptionNumber,
      validationType: profileTypeData?.typeValidation,
      tradingName: addressData?.tradingName,
      address: handleGetAddress(userType, addressData?.address, serviceData.address),
      isServiceProvider:
        typeProfile === UserProfileType.LAWYER
          ? profileTypeData?.isVerified
            ? serviceData.isProviderService
            : false
          : serviceData.isProviderService,
      specialties: serviceTypeData.specialtiesSelected.map((specialty) => specialty.id),
      correspondences: serviceTypeData.correspondencesSelected.map(
        (correspondence) => correspondence.id,
      ),
      districtsOfOperationIds: districtsSelectedsByUser.map((district) => district.id),
      profilePictureId: profileData.pictureProfile?.id,
      externalContacts: prepareExternalContactsForSubmit(profileData.externalContacts).map(
        (contact) => ({
          ...contact,
          createdById: user.id,
        }),
      ),
      presentation: profileData.presentationLetter,
      linkFacebook: profileData.linkFacebook,
      linkInstagram: profileData.linkInstagram,
      linkLinkedIn: profileData.linkLinkedIn,
      linkSite: profileData.linkSite,
      linkTwitter: profileData.linkTwitter,
      url: urlData.url,
      type: unMask(personalData.document).length === 11 ? TypeUser.USER : TypeUser.COMPANY,
      registrationOrigin: profileData.registrationOrigin,
    };
    const formData = serialize(update, { indices: true });
    if (profileData.pictureProfile?.file)
      formData.append('profilePicture', profileData.pictureProfile?.file);

    if (profileTypeData) {
      if (
        profileTypeData.verificationPictures[0]?.file ||
        profileTypeData.verificationPictures[1]?.file
      ) {
        if (profileTypeData.verificationPictures[0]?.file)
          formData.append('verificationPictureFront', profileTypeData.verificationPictures[0].file);
        if (profileTypeData.verificationPictures[1]?.file)
          formData.append('verificationPictureBack', profileTypeData.verificationPictures[1].file);
      }
    }
    app.setLoading(true);
    UserController.completeRegister(formData)
      .then(() => {
        setFormSubmited(false);
      })
      .then(() => {
        alert.show({ message: 'Mudanças aplicadas com sucesso!', type: 'success' });
        setDocument(update.document ?? '');
        setFirstName(update.firstName ?? '');
        setLastName(update.lastName ?? '');
        setIsVerified(!!update.isVerified);
        setInscriptionNumber(update.inscriptionNumber ?? '');
        setTypeProfile(update.profileType);
        setTypeValidation(update.validationType);
        setVerificationPictures(profileTypeData?.verificationPictures ?? []);
        setAddress(update.address ?? ({} as IAddressViewModel));
        setTradingName(update.tradingName ?? '');
        setIsServiceProvider(!!update.isServiceProvider);
        setSpecialtiesSelectedByUser(serviceTypeData.specialtiesSelected);
        setCorrespondencesSelectedByUser(serviceTypeData.correspondencesSelected);
        setPresentationLetter(update.presentation ?? '');
        setPictureProfile(profileData.pictureProfile);
        setExternalContacts(profileData.externalContacts);
        setLinkFacebook(update.linkFacebook ?? '');
        setLinkInstagram(update.linkInstagram ?? '');
        setLinkLinkedIn(update.linkLinkedIn ?? '');
        setLinkSite(update.linkSite ?? '');
        setLinkTwitter(update.linkTwitter ?? '');
        setUrl(update.url ?? '');
        setActiveForm('');
        setUserType(update.type!);
        setRegistrationOrigin(update.registrationOrigin);
        const externalContactsFiltered = update?.externalContacts?.filter(
          (contact) => !!contact?.value?.trim(),
        );
        if (externalContactsFiltered && externalContactsFiltered.length > 0) {
          const contacts: NotificationPreferencesInputModel = {
            contactEmail: externalContactsFiltered.some(
              (contact) => contact.type === TypeContact.EMAIL && contact.isVerified,
            )
              ? externalContactsFiltered.find(
                  (contact) => contact.type === TypeContact.EMAIL && contact.isVerified,
                )?.value
              : undefined,
            whatsapp: externalContactsFiltered.some(
              (contact) => contact.type === TypeContact.WHATSAPP && contact.isVerified,
            )
              ? unMask(
                  externalContactsFiltered.find(
                    (contact) => contact.type === TypeContact.WHATSAPP && contact.isVerified,
                  )!.value,
                )
              : undefined,
          };
          UserController.createContactsForNotification(contacts);
        }
        setUser((prev) => {
          const newUser = {
            ...prev,
            ...update,
          };
          return newUser;
        });
        navigate('/feed');
      })
      .catch((error) => {
        if (error?.response?.data === 'INSCRIPTION_NUMBER_ALREADY_REGISTERED') {
          alert.show({
            message: 'Número de inscrição já cadastrado',
            type: 'error',
          });
        } else {
          alert.show({
            message: 'Falha ao tentar atualizar o perfil, tente novamente!',
            type: 'error',
          });
        }
      })
      .finally(() => {
        app.setLoading(false);
      });
  };

  const handleGetAddress = (
    typeUser: TypeUser,
    companyAddress?: IAddressViewModel,
    userAddress?: IAddressViewModel,
  ) => {
    if (typeUser === TypeUser.COMPANY) {
      const addressFormated = !handleGetAddressError(companyAddress).hasError
        ? companyAddress
        : undefined;
      return addressFormated;
    } else if (typeUser === TypeUser.USER) {
      const addressFormated = !handleGetAddressError(userAddress).hasError
        ? userAddress
        : undefined;
      return addressFormated;
    }
    return undefined;
  };

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.COMPLEMENT_REGISTER)) {
      setRunningTutorial(true);
    }
  }, [user?.tutorialProgress]);

  return (
    <>
      <JoyrideTour
        steps={steps}
        type={TutorialType.COMPLEMENT_REGISTER}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <NavHeader typeHeader="default" />
      <Content>
        <div className="page-title">
          <TitlePage
            title="Finalização do cadastro"
            onRequestOpen={() => {
              setRunningTutorial(true);
            }}
            id="start-item"
          />
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
          }}>
          <RegisterPersonalData ref={personalDataRef} />

          {userType === TypeUser.USER && <RegisterTypeProfile ref={profileTypeDataRef} />}
          {[TypeUser.COMPANY, TypeUser.ADM].includes(userType) && (
            <RegisterAddress ref={addressDataRef} />
          )}
          {/* {(typeProfile !== UserProfileType.LAWYER || isVerified) && ( */}
          <RegisterService ref={serviceDataRef} />
          <RegisterTypeServices ref={serviceTypeDataRef} />
          {/* )} */}
          <RegisterProfile ref={profileDataRef} />
          <RegisterUrl ref={urlDataRef} />
          <InviteFriend />
          <div className="contentButton">
            <BasicButton variant="outlined" onClick={handleUpdateProfile} id="save-button">
              Avançar
            </BasicButton>
          </div>
        </div>
      </Content>
    </>
  );
}
